<template>
    <BasicModalWindow
      @modalWindowClose="onClose()"
      class="contacts-modal"
      :title="`contacts.${selected_tab}`"
    >
        <template #pre-body>
            <div class="tabs">
                <ActionButton
                    :class="{ active: selected_tab === 'persons' }"
                    text="contacts.persons"
                    @click="onSelectTab('persons')"
                />
                <ActionButton
                    :class="{ active: selected_tab === 'groups' }"
                    text="contacts.groups"
                    @click="onSelectTab('groups')"
                />

                <ActionButton
                    :class="{ active: selected_tab === 'frequent' }"
                    text="contacts.frequent"
                    @click="onSelectTab('frequent')"
                />
            </div>

            <div class="search-row">
                <div class="selected-count"
                    @click="onUnselectAll()"
                    :class="{ clickable : contacts_selection_collection.count > 0}"
                >
                    <ActionButton v-if="contacts_selection_collection.count > 0"
                        class='outline'
                        icon="fas fa-times"
                    />
                    <span><strong>{{ contacts_selection_collection.count }}</strong> {{Language.say('contacts.selected')}}</span>
                </div>

                <InputSearch
                    ref="input_search_component"
                    class="contacts-search"
                    @inputSearchSet='onInputSearchSet'
                    @inputSearchClear='onInputSearchClear'
                />
            </div>
        </template>

        <template #body>
          <div v-if="contacts_filter_collection.count > 0">
            <div class="tabcontent list-group list-wrapper" v-if="selected_tab === 'persons' || selected_tab === 'frequent'" >
              <SmartList ref="smart_list_component"
                    @smartListItemsSet=onSmartListItemsSet
                    :smart_list_items="contacts_filter_collection.items"
                >
                    <template #elements>
                        <div v-for='contact in smart_list_items'
                        :key="'persons' + contact.id"
                        class="contact"
                        tabindex="0"
                        @click="onPersonSelection(contact)"
                        :class="{selected: contacts_selection_collection.index[contact.id]}"
                    >
                        <div class="checkbox">
                            <input
                                id="contact"
                                type="checkbox"
                                :disabled="true"
                                tabindex="-1"
                                :checked="contacts_selection_collection.index[contact.id]"
                                @click="onPersonSelection(contact)"
                            >
                            <label for="contact"></label>
                        </div>
                        <div class="meta">
                            <span class="name">{{contact.name}}</span>
                            <span class="email">{{contact.email}}</span>
                        </div>
                      </div>
                    </template>
                </SmartList>
            </div>

            <div class="tabcontent list-group list-wrapper" v-else-if="selected_tab === 'groups' ">
                <SmartList ref="smart_list_component"
                    @smartListItemsSet=onSmartListItemsSet
                    :smart_list_items="contacts_filter_collection.items"
                >
                    <template #elements>
                            <div v-for='item in smart_list_items'
                                :key="'group' + item.id"
                                class="contact"
                                tabindex="0"
                                @click="onContactselection(item)"
                                :class="{ group:item.type === 'group',
                                          'depth-2':item.type === 'person',
                                          selected: contacts_selection_collection.exists(item.id) || contacts_selection_collection.isGroupSelectedFull(item.id),
                                          halfSelected : contacts_selection_collection.isGroupSelectedHalf(item.id)
                                }"
                            >
                                <div class="checkbox">
                                    <input
                                        id="contact"
                                        type="checkbox"
                                        :disabled="true"
                                        tabindex="-1"
                                        :checked="contacts_selection_collection.index[item.id] ||
                                                  contacts_selection_collection.isGroupSelectedFull(item.id) ||
                                                  contacts_selection_collection.isGroupSelectedHalf(item.id)"
                                        @click.stop="onContactselection(item)">
                                    <label for="contact"></label>
                                </div>
                                <div class="meta">
                                    <span class="name">
                                        {{item.name}}
                                    </span>
                                    <span v-if="item.type === 'group' && item.members_count == 0" class="email">
                                        {{Language.say('contacts.select_modal.label.no_contacts')}}
                                    </span>
                                    <span class="info" v-if="item.type === 'group' && item.members_count > 0"
                                      :class=" {sub_group : item.members_count > 0 }"
                                    >
                                        {{contacts_selection_collection.groups[item.id].count}} / {{item.members_count}} {{Language.say('contacts.select_modal.label.selected_contacts')}}
                                    </span>
                                    <span v-else class="email">
                                        {{item.email}}
                                    </span>
                                </div>
                                <ActionButton v-if="item.type === 'group' && item.members_count > 0"
                                    class='clear'
                                    :icon="(expanded_groups_collection[item.id]) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                                    @click.stop="onGroupExpand(item)"
                                />
                            </div>
                    </template>
                </SmartList>
            </div>
          </div>

          <div class="height-100" v-else>
              <ContentEmpty
                  :text="'contacts.select_modal.label.no_results.' + selected_tab"
                  icon='far fa-address-card'
                  :class="{ groups: selected_tab === 'groups' }"
              />
          </div>
        </template>

        <template #footer>
            <ActionButton
                text="global.actions.cancel"
                @click="onSubmitCancel()"
            />
            <ActionButton
                @click="onSubmit()"
                class='main-action push-right'
                :class="{disabled: !contacts_selection_collection.count > 0}"
                text="global.actions.select"
            />
        </template>
    </BasicModalWindow>
</template>

<script setup>
  import { ref, reactive,  nextTick }      from 'vue';
  import { defineEmits, defineProps }      from 'vue';
  import { onBeforeMount }                 from 'vue';
  import BasicModalWindow                  from '@/vue/Layouts/Modals/BasicModalWindow.vue';
  import InputSearch                       from '@/vue/Elements/Inputs/InputSearch.vue'
  import ActionButton                      from '@/vue/Elements/Buttons/ActionButton.vue'
  import SmartList                         from '@/vue/Elements/Tools/SmartList.vue';
  import ContentEmpty                      from '@/vue/Elements/Content/ContentEmpty.vue';
  import ContactsGroupSelectionCollection  from '@/js/Contacts/Collections/ContactsGroupSelectionCollection';
  import ContactsFilterCollection          from '@/js/Contacts/Collections/ContactsFilterCollection';
  import ContentState                      from '@/js/Shared/State/ContentState';
  import Language                          from '@/js/Shared/Language/Language.js';

  const emit = defineEmits([
    'contactsSelectModalCancel', 'contactsSelectModalSubmit'
  ]);

  const props = defineProps({
    contacts_controller : {
      required: true,
    },

    selected_contacts : {
      default: {}
    },

    starting_tab : {
      default: 'persons'
    }
  });

  const contacts_selection_collection = reactive(new ContactsGroupSelectionCollection());
  const contacts_filter_collection    = reactive(new ContactsFilterCollection());
  const content_state                 = reactive(new ContentState());
  const selected_tab                  = ref(props.starting_tab)
  const smart_list_items              = ref();
  const input_search_component        = ref()
  const expanded_groups_collection    = ref({});

  onBeforeMount(async () => {
    content_state.setLoadedState(true);
    contacts_selection_collection.setContacts(props.contacts_controller.contacts);
    contacts_selection_collection.addItems(props.selected_contacts.items);
    setSelectedTab(props.starting_tab)
    setTabModifiers();
  })

  const onSmartListItemsSet = (items) => {
    setSmartListItems(items);
  }

  const onSelectTab = async (tab) => {
    setSelectedTab(tab);
    setTabModifiers();
    await nextTick();
    input_search_component.value.resetInput()
  }

  const onUnselectAll = () => {
    contacts_selection_collection.reset();
  }

  const onSubmit = () => {
    submit();
  }

  const onSubmitCancel = () => {
    close();
  }

  const onClose = () => {
    close();
  }

  const onGroupExpand = (group) => {
    toggleGroupExpand(group);
    contacts_filter_collection.reload()
  }

  const onPersonSelection = (person) => {
    contacts_selection_collection.toggle(person.id, person);
  }

  const onContactselection = (contact) => {
    (contact.type === 'person')
      ? contacts_selection_collection.toggle(contact.id, contact)
      : contacts_selection_collection.toggleGroup(contact);
  }

  const onInputSearchSet = (input) => {
    contacts_filter_collection.searchIn(selected_tab.value, input)
  }

  const onInputSearchClear = () => {
    setTabModifiers();
  }

  const setSmartListItems = (items) => {
    smart_list_items.value = items;
  }

  const setTabModifiers = () => {
    if (selected_tab.value === 'persons') {
      setModifiersPersons();
      return;
    }

    if (selected_tab.value === 'groups') {
      setModifiersGroups();
      return;
    }

    if (selected_tab.value === 'frequent') {
      setModifiersFrequent();
      return;
    }
  }

  const setModifiersPersons = () => {
    contacts_filter_collection.reset();
    contacts_filter_collection.setSearchList(props.contacts_controller.contacts.persons);
    contacts_filter_collection.setModifiers({
      'add_contact_focus' : contacts_selection_collection.items,
    });
    contacts_filter_collection.reload();
  }

  const setModifiersGroups = () => {
    contacts_filter_collection.reset();
    contacts_filter_collection.setSearchList(props.contacts_controller.contacts.groups);
    contacts_filter_collection.setModifiers({
      'type'              : 'groups',
      'add_group_members' : expanded_groups_collection.value,
    });

    contacts_filter_collection.reload();
  }

  const setModifiersFrequent = () => {
    contacts_filter_collection.reset();
    contacts_filter_collection.setSearchList(props.contacts_controller.contacts.frequent);
    contacts_filter_collection.setModifiers({
      'type': 'frequent',
    });

    contacts_filter_collection.reload();
  }

  const setSelectedTab = (tab) => {
    selected_tab.value = tab;
  }

  const toggleGroupExpand = (group) => {
    if (expanded_groups_collection.value[group.id]) {
      delete expanded_groups_collection.value[group.id];
      return;
    }

    expanded_groups_collection.value[group.id] = true
  }

  const submit = () => {
    emit('contactsSelectModalSubmit', contacts_selection_collection);
  }

  const close = () => {
    emit('contactsSelectModalCancel');
  }
</script>
